import type { TFunction } from 'i18next';
import type { Product, ProductCategory } from '@etiquetachef/types';

export type CategoryGroup = {
  category: ProductCategory;
  label: string;
  items: Product[];
};

/**
 * Agrupa produtos por categoria, ordena as categorias pelo label traduzido
 * (varia por locale) e os itens dentro de cada grupo por nome. "OTHER"
 * (catch-all) sempre fica por último — espelha `groupByCategory` de
 * `apps/app` e o `ProductsScreen`/`StockScreen` do Android. As labels
 * reutilizam as chaves compartilhadas `app.products.categoryOptions.*`.
 * Usado por: `features/products/ProductsTab.tsx`.
 */
export function groupProductsByCategory(products: readonly Product[], t: TFunction): CategoryGroup[] {
  const byCategory = new Map<ProductCategory, Product[]>();
  for (const product of products) {
    const list = byCategory.get(product.category);
    if (list === undefined) {
      byCategory.set(product.category, [product]);
    } else {
      list.push(product);
    }
  }
  const entries: CategoryGroup[] = Array.from(byCategory.entries()).map(([category, list]) => ({
    category,
    label: t(`app.products.categoryOptions.${category}`),
    items: list.slice().sort((a, b) => a.name.localeCompare(b.name)),
  }));
  const other = entries.filter((g) => g.category === 'OTHER');
  const rest = entries
    .filter((g) => g.category !== 'OTHER')
    .sort((a, b) => a.label.localeCompare(b.label));
  return [...rest, ...other];
}
